/**
 * BenevitaLogo — the redesigned Benevita "home health care" mark.
 *
 * A clean, scalable vector of the brand idea: a maroon house whose heart
 * carries a gold heartbeat, riding a gold + silver swoosh. Crisp at any
 * size (no raster), and works on light or dark backgrounds.
 *
 * Props:
 *   size        height in px of the mark (width scales)        default 40
 *   variant     'mark' | 'full'  (full adds the wordmark)      default 'mark'
 *   onDark      true => light house/text for dark backgrounds  default false
 *   tagline     show "home health care" under the wordmark (full only)
 */
import React from 'react';

const MAROON = '#7b1e3c';
const MAROON_DK = '#7b1e3c';
const GOLD = '#c39a4e';
const GOLD_LT = '#e3c77e';
const SILVER = '#aeb6bd';
const GRAY = '#5e4a53';

export default function BenevitaLogo({
  size = 40,
  variant = 'mark',
  onDark = false,
  tagline = true,
  style,
}) {
  const house = onDark ? '#ffffff' : MAROON;
  const houseDeep = onDark ? '#f0d9df' : MAROON_DK;
  const heart = onDark ? GOLD_LT : MAROON;
  const word = onDark ? '#ffffff' : MAROON;
  const sub = onDark ? SILVER : GRAY;
  const uid = `bnv${size}${variant}${onDark ? 'd' : 'l'}`;

  // The mark: viewBox 0 0 72 64 (house + heart + swoosh)
  const Mark = (
    <svg width={(size * 72) / 64} height={size} viewBox="0 0 72 64" fill="none"
         xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Benevita">
      <defs>
        <linearGradient id={`${uid}-gold`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={GOLD_LT} />
          <stop offset="100%" stopColor={GOLD} />
        </linearGradient>
      </defs>

      {/* gold + silver swoosh under the house */}
      <path d="M2 54 C 16 46, 30 46, 46 50 C 56 52, 64 52, 70 49"
            stroke={SILVER} strokeWidth="3.4" strokeLinecap="round" fill="none" opacity="0.9" />
      <path d="M2 58 C 16 50, 32 50, 48 54 C 58 56, 65 56, 70 53"
            stroke={`url(#${uid}-gold)`} strokeWidth="3.4" strokeLinecap="round" fill="none" />

      {/* house: roof (chevron) + chimney */}
      <path d="M6 30 L34 8 L62 30" stroke={house} strokeWidth="5"
            strokeLinejoin="round" strokeLinecap="round" fill="none" />
      <rect x="11" y="14" width="6" height="13" rx="1.5" fill={house} />

      {/* house walls (open base, sits on swoosh) */}
      <path d="M12 31 L12 44 M56 31 L56 44" stroke={house} strokeWidth="5"
            strokeLinecap="round" />

      {/* window */}
      <rect x="26.5" y="20" width="11" height="11" rx="1.5" fill={houseDeep} opacity="0.92" />
      <path d="M32 20 L32 31 M26.5 25.5 L37.5 25.5" stroke={onDark ? MAROON : '#ffffff'} strokeWidth="1.4" />

      {/* heart with heartbeat */}
      <path d="M34 50 C 27 44, 22 40, 22 35.5 C 22 32.4, 24.4 30, 27.4 30
               C 29.6 30, 31.4 31.2, 34 33.6 C 36.6 31.2, 38.4 30, 40.6 30
               C 43.6 30, 46 32.4, 46 35.5 C 46 40, 41 44, 34 50 Z"
            fill={heart} />
      <path d="M24 39 H30 L32 35 L34.5 43 L37 38 H44"
            stroke={onDark ? MAROON : GOLD_LT} strokeWidth="2" strokeLinecap="round"
            strokeLinejoin="round" fill="none" />
    </svg>
  );

  if (variant === 'mark') {
    return <span style={{ display: 'inline-flex', alignItems: 'center', ...style }}>{Mark}</span>;
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: size * 0.28, ...style }}>
      {Mark}
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1, justifyContent: 'center' }}>
        <span style={{
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: size * 0.62, fontWeight: 400, color: word, letterSpacing: '-0.5px',
        }}>benevita</span>
        {tagline && (
          <span style={{
            fontSize: size * 0.22, fontWeight: 500, color: sub,
            letterSpacing: size * 0.06, textTransform: 'lowercase', marginTop: size * 0.07,
          }}>home health care</span>
        )}
      </span>
    </span>
  );
}
